import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { can, errorMessage, listTrash, restoreTrash, type TrashItem, type User } from './api';
import { formatWhen } from './format';

export function TrashPage({ user, projectId }: { user: User; projectId: number | null }) {
  const [rows, setRows] = useState<TrashItem[]>([]);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [restored, setRestored] = useState<number | null>(null);
  const permitted = can(user, 'ticket.delete');
  useEffect(() => {
    if (!permitted) return;
    let cancelled = false;
    void listTrash('tickets', projectId).then((items) => {
      if (!cancelled) { setRows(items); setMore(items.length === 30); setError(''); }
    }).catch((err: unknown) => { if (!cancelled) setError(errorMessage(err)); });
    return () => { cancelled = true; };
  }, [projectId, permitted]);
  async function restore(id: number) {
    setBusy(true); setError('');
    try {
      await restoreTrash('tickets', id); setRows((prev) => prev.filter((row) => row.id !== id));
      setRestored(id);
    } catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  }
  async function loadMore() {
    setBusy(true);
    try {
      const items = await listTrash('tickets', projectId, rows.at(-1)?.id);
      setRows((prev) => [...prev, ...items]); setMore(items.length === 30);
    } catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  }
  if (!permitted) return <p>휴지통 관리 권한이 없다.</p>;
  return <div className="flex flex-col gap-s4">
    <h1 className="m-0 text-h1">휴지통</h1>
    {error && <p role="alert">{error}</p>}
    {restored !== null && <p role="status">복원했다. <Link className="text-brand underline" to={`/tickets/${restored}`}>복원한 티켓 열기</Link></p>}
    {!error && rows.length === 0 && <p>휴지통이 비어 있다.</p>}
    {rows.map((row) => <div key={row.id} className="flex items-center gap-s3 rounded-md border border-line bg-panel p-s3">
      <div className="min-w-0 flex-1"><strong>#{row.id} · {row.title}</strong><p className="m-0 text-small text-ink-3">삭제 {formatWhen(row.deleted_at)}</p></div>
      <Button disabled={busy} onClick={() => void restore(row.id)}>복원</Button>
    </div>)}
    {more && <Button variant="outline" disabled={busy} onClick={() => void loadMore()}>더 보기</Button>}
  </div>;
}
